import React from "react";
import {
  ArrowRight,
  FileSearch,
  MessagesSquare,
  MonitorCheck,
  TriangleAlert,
} from "lucide-react";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { BotSuccessIcon } from "./BotIcons";
import { useTranslation } from "../../../hooks/useTranslation";

// Azioni consigliate post-creazione (design 4041:24994). Nel mockup la
// prima card è mostrata in verde come ESEMPIO dello stato hover: il verde
// è l'hover di tutte e tre, non uno stato fisso.
const QUICK_ACTIONS = [
  {
    key: "actionPlayground",
    icon: MessagesSquare,
    path: (id) => `/bots/${id}/playground`,
  },
  {
    key: "actionWidget",
    icon: MonitorCheck,
    path: (id) => `/bots/${id}/widget`,
  },
  {
    key: "actionSources",
    icon: FileSearch,
    path: (id) => `/bots/${id}/sources`,
  },
];

/**
 * Ultima pagina del flusso custom — "Registrazione - success"
 * (frame Figma 4041:24994): icona bot verde, titolo, card con tre
 * azioni rapide e CTA "Vai alla dashboard".
 */
const CreationSuccess = ({ bot, sourcesWarning, onNavigate }) => {
  const { t } = useTranslation();

  return (
    <div className="pu-success-in -mx-4 -mb-4 flex min-h-[calc(100vh-4rem)] flex-col items-center bg-[#F9FAFB] px-4 pt-14">
      <style>{`
        @keyframes puSuccessIn { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        .pu-success-in { animation: puSuccessIn .5s ease-out both; }
        @media (prefers-reduced-motion: reduce) { .pu-success-in { animation: none; } }
      `}</style>

      <BotSuccessIcon />

      <h1 className="mt-5 text-center text-[32px] font-semibold leading-9 text-[#020617]">
        {t("onboardingWizard.successTitle")}
      </h1>
      <p className="mt-4 text-center text-base leading-7 text-[#64748B]">
        {t("onboardingWizard.successSubtitle")}
      </p>

      {/* Il bot è nato ma le fonti no: dirlo, invece di far credere che sia
          filato tutto liscio (prima l'errore finiva solo in console). */}
      {sourcesWarning && (
        <div className="mt-8 flex w-full max-w-[640px] items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-left">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div>
            <p className="text-sm font-semibold text-amber-900">
              {t("onboardingWizard.sourcesFailedTitle")}
            </p>
            <p className="mt-0.5 text-sm text-amber-800">{sourcesWarning}</p>
            <button
              type="button"
              onClick={() => onNavigate(`/bots/${bot._id}/sources`)}
              className="mt-1.5 text-sm font-semibold text-amber-900 underline underline-offset-2"
            >
              {t("onboardingWizard.sourcesFailedCta")}
            </button>
          </div>
        </div>
      )}

      <Card className="mt-10 w-full max-w-[640px] rounded-[14px] shadow-[0_5px_5px_rgba(0,0,0,0.1)]">
        <CardContent className="p-6">
          <p className="text-center text-base font-semibold text-[#020617]">
            {t("onboardingWizard.quickActionsTitle")}
          </p>

          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
            {QUICK_ACTIONS.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.key}
                  type="button"
                  onClick={() => onNavigate(action.path(bot._id))}
                  className="group rounded-lg border border-[#E2E8F0] p-4 text-left transition-colors hover:border-[#19D163] hover:bg-[#19D163]/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Icon className="h-5 w-5 text-[#020617] transition-colors group-hover:text-[#19D163]" />
                  <p className="mt-3 text-sm font-semibold text-[#020617]">
                    {t(`onboardingWizard.${action.key}Title`)}
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    {t(`onboardingWizard.${action.key}Desc`)}
                  </p>
                </button>
              );
            })}
          </div>

          <Button
            className="mt-6 w-full"
            size="lg"
            onClick={() => onNavigate("/home")}
          >
            {t("onboardingWizard.goToDashboard")}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreationSuccess;
